import React from 'react';
import { Input } from 'reactstrap';
import { TermForm } from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { TermsFormSelect } from './TermsFormSelect';
import {
    getOptionsForAnswerType,
    LoiFormAnswerType,
    REAL_PROPERTY_OPTIONS,
} from '../../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';

export interface TermsFormPropertyPurchasedBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    onAdminCommentChange?: (fieldKey: string, adminComment: string | null) => void;
    isAdmin?: boolean;
}

export const TermsFormPropertyPurchasedBlock: React.FC<
    TermsFormPropertyPurchasedBlockProps
> = ({
    termForm,
    readOnly,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    fieldComments,
    onFieldCommentChange,
    onAdminCommentChange,
    isAdmin,
}) => {
    const update = (patch: Partial<TermForm>) => {
        onChange?.({ ...termForm, ...patch });
    };

    const getComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.buyerComment ?? null;
    const getAdminComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.adminComment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && !readOnly;
    const showAdminComment = showComments || isAdminCommentEditable;

    const commentCell = (fieldKey: string) => {
        if (!showComments) return null;
        const comment = getComment(fieldKey);
        return (
            <td className="terms-form-col-comment">
                {(
                    <Input
                        type="textarea"
                        value={comment ?? ''}
                        readOnly={!isBuyerReview}
                        onChange={(e) =>
                            onFieldCommentChange?.(
                                fieldKey,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="terms-form-comment-input"
                        aria-label={`Comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };

    const adminCommentCell = (fieldKey: string) => {
        if (!showAdminComment) return null;
        const adminComment = getAdminComment(fieldKey);
        return (
            <td className="terms-form-col-comment terms-form-col-admin-comment">
                {(
                    <Input
                        type="textarea"
                        value={adminComment ?? ''}
                        readOnly={!isAdminCommentEditable}
                        onChange={(e) =>
                            onAdminCommentChange?.(
                                fieldKey,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="terms-form-comment-input"
                        aria-label={`Admin comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };

    return (
        <div className="terms-form-detail-block terms-form-section-underlined">
            <div className="property-purchased-table terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th style={{ width: '195px', minWidth: '195px' }}>
                                Property Purchased
                            </th>
                            <th style={{ width: '155px', minWidth: '155px' }}>
                                Values
                            </th>
                            {showAdminComment && (
                                <th className="terms-form-col-comment terms-form-col-admin-comment">
                                    Admin Comment
                                </th>
                            )}
                            {showComments && (
                                <th className="terms-form-col-comment">
                                    Buyer Comment
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="terms-form-col-item table-label-cell">
                                Practice Property Purchased
                            </td>
                            <td className="terms-form-col-value">
                                <TermsFormSelect
                                    value={
                                        termForm.propertyPurchased
                                            ?.practicePropertyPurchased ?? ''
                                    }
                                    options={getOptionsForAnswerType(
                                        LoiFormAnswerType.PurchaseAgreementType
                                    )}
                                    disabled={readOnly}
                                    invalid={errors?.has(
                                        'practicePropertyPurchased'
                                    )}
                                    onChange={(e) =>
                                        update({
                                            propertyPurchased: {
                                                ...termForm.propertyPurchased,
                                                practicePropertyPurchased: e,
                                            },
                                        })
                                    }
                                    aria-label="Practice Property Purchased"
                                />
                            </td>
                            {adminCommentCell(
                                LoiTermFormFieldKeys.practicePropertyPurchased
                            )}
                            {commentCell(
                                LoiTermFormFieldKeys.practicePropertyPurchased
                            )}
                        </tr>
                        <tr>
                            <td className="terms-form-col-item table-label-cell">
                                Practice Real Property Included
                            </td>
                            <td className="terms-form-col-value">
                                <TermsFormSelect
                                    value={
                                        termForm.propertyPurchased
                                            ?.practiceRealPropertyInclude ?? ''
                                    }
                                    options={REAL_PROPERTY_OPTIONS}
                                    disabled={readOnly}
                                    invalid={errors?.has(
                                        'practiceRealPropertyInclude'
                                    )}
                                    onChange={(e) =>
                                        update({
                                            propertyPurchased: {
                                                ...termForm.propertyPurchased,
                                                practiceRealPropertyInclude: e,
                                            },
                                        })
                                    }
                                    aria-label="Practice Real Property Included"
                                />
                            </td>
                            {adminCommentCell(
                                LoiTermFormFieldKeys.practiceRealPropertyInclude
                            )}
                            {commentCell(
                                LoiTermFormFieldKeys.practiceRealPropertyInclude
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
